import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import Card from 'components/Card';
import EducationDrawer from 'components/EducationDrawer';
import {
  HeaderContainer,
  SidebarTitle,
  SegmentList,
  SegmentItem,
  SegmentInfo,
  SegmentLabel,
  SegmentDescription,
  SegmentCount,
  ProTipCard,
  ProTipHeader,
  ProTipIconBox,
  ProTipTitle,
  ProTipText,
} from './StyledComponents';

const SegmentBreakdown = ({ segments, onOpenSmartGuide, loading }) => {
  const [showEducation, setShowEducation] = useState(false);
  const theme = useContext(ThemeContext);

  const defaultSegments = [
    {
      label: 'Super Fan',
      description: '10+ events, 75%+ attendance',
      count: 342,
      color: theme?.colors?.segments?.superFan || '#22C55E',
      background: theme?.colors?.segments?.superFan
        ? `${theme.colors.segments.superFan}1A`
        : 'rgba(34, 197, 94, 0.1)',
    },
    {
      label: 'Regular Fan',
      description: '5+ events, 50%+ attendance',
      count: 687,
      color: theme?.colors?.segments?.regularFan || '#3B82F6',
      background: 'rgba(59, 130, 246, 0.1)',
    },
    {
      label: 'Casual Fan',
      description: '2-4 events',
      count: 1124,
      color: theme?.colors?.segments?.casualFan || '#06B6D4',
      background: 'rgba(6, 182, 212, 0.1)',
    },
    {
      label: 'Occasional',
      description: 'Infrequent visitors, still active',
      count: 234,
      color: theme?.colors?.segments?.occasional || '#A78BFA',
      background: 'rgba(167, 139, 250, 0.1)',
    },
    {
      label: 'First-Timer',
      description: '1 event',
      count: 542,
      color: theme?.colors?.segments?.firstTimer || '#F59E0B',
      background: 'rgba(245, 158, 11, 0.1)',
    },
    {
      label: 'Inactive',
      description: 'No events or 14+ months inactive',
      count: 152,
      color: theme?.colors?.segments?.inactive || '#DC2626',
      background: 'rgba(220, 38, 38, 0.1)',
    },
  ];

  const displaySegments = segments || defaultSegments;

  // Show loading state if data is loading
  if (loading && !segments) {
    return (
      <Card
        style={{
          position: 'sticky',
          top: '20px',
        }}
      >
        <HeaderContainer>
          <SidebarTitle>Segment Breakdown</SidebarTitle>
        </HeaderContainer>
        <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6 }}>
          Loading segments...
        </div>
      </Card>
    );
  }

  return (
    <Card
      style={{
        position: 'sticky',
        top: '20px',
      }}
    >
      <HeaderContainer>
        <SidebarTitle>Segment Breakdown</SidebarTitle>
        <EducationDrawer
          title="Understanding Customer Segments"
          isOpen={showEducation}
          onOpen={() => setShowEducation(true)}
          onClose={() => setShowEducation(false)}
          helpButtonLabel="Learn about customer segments"
        >
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
              Customer segments help you understand your fan base and tailor
              your marketing efforts. Each segment is automatically calculated
              based on attendance patterns since becoming a customer.
            </p>
          </div>

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <div
              style={{
                borderLeft: `4px solid ${
                  theme?.colors?.segments?.superFan || '#22C55E'
                }`,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                background: theme?.colors?.segments?.superFan
                  ? `${theme.colors.segments.superFan}0D`
                  : 'rgba(34, 197, 94, 0.05)',
                borderRadius: '8px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 8px 0',
                  color: theme?.colors?.segments?.superFan || '#22C55E',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                Super Fan
              </h4>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                Attended 10+ events with 75%+ attendance rate. Your most loyal
                customers.
              </p>
            </div>

            <div
              style={{
                borderLeft: `4px solid ${
                  theme?.colors?.segments?.regularFan || '#3B82F6'
                }`,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                background: 'rgba(59, 130, 246, 0.05)',
                borderRadius: '8px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 8px 0',
                  color: theme?.colors?.segments?.regularFan || '#3B82F6',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                Regular Fan
              </h4>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                Attended 5+ events with 50%+ attendance rate. Consistent
                supporters.
              </p>
            </div>

            <div
              style={{
                borderLeft: `4px solid ${
                  theme?.colors?.segments?.casualFan || '#06B6D4'
                }`,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                background: 'rgba(6, 182, 212, 0.05)',
                borderRadius: '8px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 8px 0',
                  color: theme?.colors?.segments?.casualFan || '#06B6D4',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                Casual Fan
              </h4>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                Attended 2-4 events. Still getting to know your venue.
              </p>
            </div>

            <div
              style={{
                borderLeft: `4px solid ${
                  theme?.colors?.segments?.occasional || '#A78BFA'
                }`,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                background: 'rgba(167, 139, 250, 0.05)',
                borderRadius: '8px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 8px 0',
                  color: theme?.colors?.segments?.occasional || '#A78BFA',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                Occasional
              </h4>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                Infrequent visitors who are still active. Last attended within
                14 months.
              </p>
            </div>

            <div
              style={{
                borderLeft: `4px solid ${
                  theme?.colors?.segments?.firstTimer || '#F59E0B'
                }`,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                background: 'rgba(245, 158, 11, 0.05)',
                borderRadius: '8px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 8px 0',
                  color: theme?.colors?.segments?.firstTimer || '#F59E0B',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                First-Timer
              </h4>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                Attended exactly one event. Great candidates for re-engagement
                campaigns.
              </p>
            </div>

            <div
              style={{
                borderLeft: `4px solid ${
                  theme?.colors?.segments?.inactive || '#DC2626'
                }`,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                background: 'rgba(220, 38, 38, 0.05)',
                borderRadius: '8px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 8px 0',
                  color: theme?.colors?.segments?.inactive || '#DC2626',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                Inactive
              </h4>
              <p
                style={{
                  margin: '0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                No purchase in over 14 months or never attended. May need
                win-back campaigns.
              </p>
            </div>
          </div>

          <ProTipCard>
            <ProTipHeader>
              <ProTipIconBox>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                >
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </ProTipIconBox>
              <ProTipTitle>Pro Tip</ProTipTitle>
            </ProTipHeader>
            <ProTipText>
              Use these segments to create targeted campaigns.
            </ProTipText>
            <button
              onClick={() => {
                setShowEducation(false);
                if (onOpenSmartGuide) {
                  setTimeout(() => {
                    onOpenSmartGuide();
                  }, 300);
                }
              }}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '13px',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
                transition: 'color 0.2s',
                color: theme?.colors?.primary || '#fa4616',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#ff6b3d';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = theme?.colors?.primary || '#fa4616';
              }}
            >
              To learn more, check out the Smart Marketing Guide →
            </button>
          </ProTipCard>
        </EducationDrawer>
      </HeaderContainer>

      <SegmentList>
        {displaySegments.map((segment, index) => (
          <SegmentItem key={index} color={segment.color}>
            <SegmentInfo>
              <SegmentLabel color={segment.color}>{segment.label}</SegmentLabel>
              <SegmentDescription>{segment.description}</SegmentDescription>
            </SegmentInfo>
            <SegmentCount color={segment.color}>
              {segment.count.toLocaleString()}
            </SegmentCount>
          </SegmentItem>
        ))}
      </SegmentList>
    </Card>
  );
};

SegmentBreakdown.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    })
  ),
  onOpenSmartGuide: PropTypes.func,
  loading: PropTypes.bool,
};

export default SegmentBreakdown;
